import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { take } from 'rxjs/operators';
import { DynamicComponentDefinition } from 'shared/components/dynamic-component-wrapper/dynamic-component.interface';
import { HasSubscriptions } from 'shared/components/higher-order/has-subscriptions';
import { DropdownOptions, DynamicDropdownCellMenuItem } from './dropdown-cell.interface';

@Component({
  selector: 'gms-dynamic-dropdown-cell',
  templateUrl: './dropdown-cell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicDropdownComponent extends HasSubscriptions implements OnInit, OnDestroy {
  @Input() data: DropdownOptions;
  menuItems: DynamicDropdownCellMenuItem[] = [];

  static componentDefinition(data: DropdownOptions): DynamicComponentDefinition {
    return {
      dynamicComponentType: DynamicDropdownComponent,
      data: data,
    };
  }

  constructor(public cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    // maintains backward compatibility with static lists
    if (this.data.data) {
      this.menuItems = this.data.data;
    }
    this.addSubscription(
      (this.data.group.controls[
        this.data.controlName
      ] as UntypedFormControl).statusChanges.subscribe(() => this.cd.markForCheck())
    );
    if (this.data.filterable) {
      this.addControlSubscription();
    }

    if (this.data.retainDataObservable) {
      this.manageMenuSubscriptionPersistently();
    }
  }

  public valueChanged(event) {
    if (this.data.valueChanged) {
      this.data.valueChanged(event);
    }
  }

  public dropDownFocused() {
    if (this.data.dropDownFocused) {
      this.data.dropDownFocused();
    }
  }

  public opened() {
    if (this.data.opened) {
      this.data.opened();
    }
  }

  public filterChanged(event, controlName) {
    if (this.data.filterChanged) {
      this.data.filterChanged(event, controlName);
    }

    this.manageMenuSubscription();
  }

  ngOnDestroy() {
    return super.ngOnDestroy();
  }

  private manageMenuSubscription() {
    if (this.data.data$) {
      const menuItems$ = this.data.data$;
      menuItems$.pipe(take(1)).subscribe(menuItems => {
        this.menuItems = menuItems;

        // Code Smell: we shouldn't need this but without it the dropdown component
        // is rendering the result set prior to this emission?
        this.cd.markForCheck();
      });
    }
  }

  private manageMenuSubscriptionPersistently() {
    if (this.data.data$) {
      const menuItems$ = this.data.data$;
      menuItems$.subscribe(menuItems => {
        this.menuItems = menuItems;

        // Code Smell: we shouldn't need this but without it the dropdown component
        // is rendering the result set prior to this emission?
        this.cd.markForCheck();
      });
    }
  }

  private addControlSubscription() {
    if (this.data.group && this.data.controlName) {
      this.addSubscription(
        (this.data.group.controls[
          this.data.controlName
        ] as UntypedFormControl).valueChanges.subscribe(() => {
          // clear menu after selection was made or control was updated
          this.menuItems = [];
        })
      );
    }
  }
}
